import { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BuildingSwitcher from '../components/BuildingSwitcher';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  Calendar,
  CreditCard,
  Users,
  Settings,
  LogOut,
  Search,
  MapPin,
  Home,
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronRight,
  BarChart3,
  MessageSquare,
  DollarSign,
  Menu,
  X,
  RefreshCw,
  ClipboardList,
  Shield,
  Palette,
  ShieldCheck,
  Inbox,
  Plus,
  TrendingUp,
  Loader2,
  Gift,
  UserCog,
  Trash2,
  Wallet,
  Lock,
  ShoppingCart,
  ArrowRightLeft,
  Vote,
  Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sidebarItems = [
  { icon: BarChart3, label: 'Dashboard', path: '/agent', needsBuilding: false },
  { icon: Building2, label: 'Buildings', path: '/agent', needsBuilding: false },
  { icon: Vote, label: 'Voting', path: '/admin/voting', needsBuilding: false },
  { icon: Mail, label: 'Invitations', path: '/agent/invitations', needsBuilding: false },
  { icon: UserCog, label: 'Pending Agent Requests', path: '/agent/agent-requests', needsBuilding: false, adminOnly: true },
  { icon: Wallet, label: 'Building Payments', path: '/agent/building-payments', needsBuilding: false },
  { icon: CreditCard, label: 'Subscriptions', path: '/agent/subscriptions', needsBuilding: false, adminOnly: true },
  { icon: UserCog, label: 'Company Management', path: '/agent/company-management', needsBuilding: false, adminOnly: true },
  { icon: Gift, label: 'Partner Program', path: '/agent/partner', needsBuilding: false },
  { icon: TrendingUp, label: 'Sales', path: '/agent/sales', needsBuilding: false },
  { icon: Wrench, label: 'Tickets', path: '/agent/building/{hoaId}/tickets', needsBuilding: true },
  { icon: DollarSign, label: 'Financials', path: '/agent/building/{hoaId}/financials', needsBuilding: true },
  { icon: ShieldCheck, label: 'Insurance', path: '/agent/insurance/{hoaId}', needsBuilding: true },
  { icon: Calendar, label: 'Bookings', path: '/agent/building/{hoaId}/bookings', needsBuilding: true },
  { icon: FileText, label: 'Documents', path: '/agent/building/{hoaId}/documents', needsBuilding: true },
  { icon: Bell, label: 'Notices', path: '/agent/building/{hoaId}/notices', needsBuilding: true },
  { icon: ClipboardList, label: 'Reports', path: '/agent/reports', needsBuilding: false },
  { icon: Shield, label: 'Compliance Alerts', path: '/agent/compliance-alerts', needsBuilding: false },
  { icon: Palette, label: 'Branding', path: '/agent/branding', needsBuilding: false },
  { icon: Users, label: 'Staff', path: '/agent/staff', needsBuilding: false },
  { icon: ShieldCheck, label: 'User Management', path: '/agent/user-management', needsBuilding: false },
  { icon: Inbox, label: 'Building Invitations', path: '/agent/building-invitations', needsBuilding: false },
  { icon: Settings, label: 'Settings', path: '/agent/settings', needsBuilding: false },
];

export default function ManagingAgentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [bounceAlertCount, setBounceAlertCount] = useState(0);
  const [showAddBuildingDialog, setShowAddBuildingDialog] = useState(false);
  const [addingBuilding, setAddingBuilding] = useState(false);
  const [newBuilding, setNewBuilding] = useState({
    name: '',
    address: '',
    city: '',
    total_units: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
  });
  const [cartItemCount, setCartItemCount] = useState(0);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferAction, setTransferAction] = useState(null);
  const [transferNotes, setTransferNotes] = useState('');
  const [processingTransfer, setProcessingTransfer] = useState(false);

  useEffect(() => {
    fetchBuildings();
    fetchBounceAlertCount();
    fetchCartCount();
    fetchPendingTransfers();
  }, []);

  const fetchCartCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/building-payments/cart`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCartItemCount(data.items?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const fetchPendingTransfers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/building-transfers/pending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingTransfers(data);
      }
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const handleTransferAction = async (action) => {
    if (!selectedTransfer) return;
    
    setProcessingTransfer(true);
    try {
      const endpoint = action === 'accept' 
        ? `/api/building-transfers/accept/${selectedTransfer.transfer_id}`
        : `/api/building-transfers/reject/${selectedTransfer.transfer_id}`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: transferNotes })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTransferModal(false);
        setSelectedTransfer(null);
        setTransferNotes('');
        fetchPendingTransfers();
        if (action === 'accept') {
          fetchBuildings(); // Refresh buildings list
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} transfer`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} transfer`);
    } finally {
      setProcessingTransfer(false);
    }
  };

  const fetchBounceAlertCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/agent-features/bounce-alerts/count`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBounceAlertCount(data.total_unresolved || 0);
      }
    } catch (error) {
      console.error('Error fetching bounce alerts:', error);
    }
  };

  const fetchBuildings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/managing-agents/my-buildings`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
        if (data.length > 0 && !selectedBuilding) {
          setSelectedBuilding(data[0].hoa_id);
        }
      } else {
        // Fetch failed (usually 401/403 for brand-new agents with no buildings yet).
        // Leave buildings empty so the zero-building guard sends them to /find-building
        // instead of showing bogus demo data.
        setBuildings([]);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteBuilding = async (hoaId, buildingName) => {
    if (!window.confirm(`Are you sure you want to remove "${buildingName}" from your managed buildings?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/managing-agents/buildings/${hoaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success(`${buildingName} removed successfully`);
        fetchBuildings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove building');
      }
    } catch (error) {
      toast.error('Failed to remove building');
    }
  };

  const handleBuildingSelect = (hoaId) => {
    setSelectedBuilding(hoaId);
    // Navigate to building workspace
    navigate(`/agent/building/${hoaId}`);
  };

  const handleAddBuilding = async () => {
    if (!newBuilding.name || !newBuilding.address || !newBuilding.total_units || !newBuilding.admin_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingBuilding(true);
    try {
      const response = await fetch(`${API_URL}/api/managing-agents/buildings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newBuilding,
          total_units: parseInt(newBuilding.total_units),
          commission_rate: 10, // 10% commission on subscription
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Building added! Commission: 10% (R${(parseInt(newBuilding.total_units) * 35 * 0.10).toFixed(0)}/year)`);
        setShowAddBuildingDialog(false);
        setNewBuilding({ name: '', address: '', city: '', total_units: '', admin_name: '', admin_email: '', admin_phone: '' });
        fetchBuildings();
      } else {
        // Demo mode - add locally
        const demoBuilding = {
          hoa_id: 'new_' + Date.now(),
          name: newBuilding.name,
          address: newBuilding.address + ', ' + newBuilding.city,
          country: 'South Africa',
          stats: { 
            open_tickets: 0, 
            recent_notices: 0, 
            total_documents: 0, 
            total_units: parseInt(newBuilding.total_units), 
            pending_payments: 0 
          },
          subscription_status: 'pending',
          commission_rate: 10,
          commission_amount: parseInt(newBuilding.total_units) * 35 * 0.10,
        };
        setBuildings([...buildings, demoBuilding]);
        toast.success(`Building added! Expected commission: R${(parseInt(newBuilding.total_units) * 35 * 0.10).toFixed(0)}/year when subscribed`);
        setShowAddBuildingDialog(false);
        setNewBuilding({ name: '', address: '', city: '', total_units: '', admin_name: '', admin_email: '', admin_phone: '' });
      }
    } catch (error) {
      // Demo mode fallback
      const demoBuilding = {
        hoa_id: 'new_' + Date.now(),
        name: newBuilding.name,
        address: newBuilding.address + ', ' + newBuilding.city,
        country: 'South Africa',
        stats: { 
          open_tickets: 0, 
          recent_notices: 0, 
          total_documents: 0, 
          total_units: parseInt(newBuilding.total_units), 
          pending_payments: 0 
        },
        subscription_status: 'pending',
        commission_rate: 10,
        commission_amount: parseInt(newBuilding.total_units) * 35 * 0.10,
      };
      setBuildings([...buildings, demoBuilding]);
      toast.success(`Building added! Expected commission: R${(parseInt(newBuilding.total_units) * 35 * 0.10).toFixed(0)}/year when subscribed`);
      setShowAddBuildingDialog(false);
      setNewBuilding({ name: '', address: '', city: '', total_units: '', admin_name: '', admin_email: '', admin_phone: '' });
    } finally {
      setAddingBuilding(false);
    }
  };

  const filteredBuildings = buildings.filter(b =>
    b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalStats = buildings.reduce((acc, b) => ({
    open_tickets: acc.open_tickets + (b.stats?.open_tickets || 0),
    total_units: acc.total_units + (b.stats?.total_units || 0),
    total_documents: acc.total_documents + (b.stats?.total_documents || 0),
    pending_payments: acc.pending_payments + (b.stats?.pending_payments || 0),
  }), { open_tickets: 0, total_units: 0, total_documents: 0, pending_payments: 0 });

  // Check if a building is locked (unpaid)
  const isBuildingLocked = (building) => {
    return building.payment_status !== 'paid' && building.status !== 'active';
  };

  // Brand-new managing agent with zero buildings → send them to the find/create flow
  // instead of rendering a blank shell.
  if (!loading && buildings.length === 0) {
    return <Navigate to="/find-building" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-indigo-900 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-indigo-800">
            <Link to="/agent" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="BodyCorp365" className="w-10 h-10" />
              <div>
                <p className="font-bold text-white">BodyCorp365</p>
                <p className="text-xs text-indigo-300">Managing Agent</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden text-indigo-300" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Building Switcher */}
          <div className="p-4 border-b border-indigo-800">
            <Select value={selectedBuilding || ''} onValueChange={handleBuildingSelect}>
              <SelectTrigger className="bg-indigo-800 border-indigo-700 text-white" data-testid="building-switcher">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select Building" />
              </SelectTrigger>
              <SelectContent>
                {buildings.map(b => (
                  <SelectItem key={b.hoa_id} value={b.hoa_id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems
                .filter(item => !item.adminOnly || user?.role === 'agent_admin' || user?.role === 'platform_owner')
                .map((item) => {
                const isActive = item.path === '/agent' && window.location.pathname === '/agent';
                let fullPath = item.path;
                
                // Replace {hoaId} placeholder with actual selected building
                if (item.needsBuilding && selectedBuilding) {
                  fullPath = item.path.replace('{hoaId}', selectedBuilding);
                }
                
                const handleClick = (e) => {
                  if (item.needsBuilding && !selectedBuilding) {
                    e.preventDefault();
                    toast.error('Please select a building first');
                    return;
                  }
                  navigate(fullPath);
                };

                return (
                  <li key={item.label}>
                    <button
                      onClick={handleClick}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-indigo-500 text-white'
                          : 'text-indigo-200 hover:text-white hover:bg-indigo-800'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium flex-1 text-left">{item.label}</span>
                      {item.label === 'Reports' && bounceAlertCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
                          {bounceAlertCount}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-4 border-t border-indigo-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Agent'}</p>
                <p className="text-xs text-indigo-300 truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-indigo-300 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-indigo-900">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" className="text-white" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <BuildingSwitcher compact />
          <Button variant="ghost" size="icon" className="text-white" onClick={fetchBuildings}>
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {/* Page Header with Cart */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Managing Agent Dashboard</h1>
              <p className="text-slate-500 mt-1">Overview of all your managed properties</p>
            </div>
            
            {/* Shopping Cart Button */}
            <Button
              variant="outline"
              className="relative"
              onClick={() => navigate('/agent/building-payments')}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Cart
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Button>
          </div>

          {/* Pending Transfer Requests Banner */}
          {pendingTransfers.length > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-transfers-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">
                      {pendingTransfers.length} Building Transfer Request{pendingTransfers.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-amber-700">
                      {pendingTransfers.length === 1 
                        ? `${pendingTransfers[0].requesting_user_name} wants to transfer "${pendingTransfers[0].building_name}"`
                        : 'Trustees want to transfer buildings to your company'
                      }
                    </p>
                  </div>
                </div>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    setSelectedTransfer(pendingTransfers[0]);
                    setShowTransferModal(true);
                  }}
                  data-testid="review-transfer-btn"
                >
                  Review
                </Button>
              </div>
            </div>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-100 text-sm">Buildings</p>
                    <p className="text-3xl font-bold">{buildings.length}</p>
                  </div>
                  <Building2 className="w-10 h-10 text-indigo-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Open Tickets</p>
                    <p className="text-3xl font-bold">{totalStats.open_tickets}</p>
                  </div>
                  <Wrench className="w-10 h-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Total Units</p>
                    <p className="text-3xl font-bold">{totalStats.total_units}</p>
                  </div>
                  <Home className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-rose-500 to-pink-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-rose-100 text-sm">Pending Payments</p>
                    <p className="text-3xl font-bold">{totalStats.pending_payments}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-rose-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search buildings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-buildings"
            />
          </div>

          {/* Buildings Grid */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Your Buildings</h2>
              <Button onClick={() => setShowAddBuildingDialog(true)} data-testid="add-building-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Building
              </Button>
            </div>
            
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-slate-200 rounded w-2/3 mb-4" />
                      <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                      <div className="h-4 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredBuildings.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No buildings assigned</h3>
                  <p className="text-slate-500">Contact the platform administrator to get buildings assigned to your company.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBuildings.map(building => {
                  const isLocked = isBuildingLocked(building);
                  return (
                  <Card 
                    key={building.hoa_id}
                    className={`hover:shadow-lg transition-all border-2 ${isLocked ? 'border-amber-300 bg-amber-50/30' : 'border-transparent hover:border-indigo-200'}`}
                    data-testid={`building-card-${building.hoa_id}`}
                  >
                    {/* Locked Building Banner */}
                    {isLocked && (
                      <div className="bg-amber-100 border-b border-amber-200 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-amber-800">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm font-medium">Building Locked</span>
                        </div>
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/agent/building-payments');
                          }}
                          data-testid={`pay-now-${building.hoa_id}`}
                        >
                          PAY NOW
                        </Button>
                      </div>
                    )}

                    {isLocked && (
                      <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
                        <p className="text-xs text-amber-700">Please settle outstanding payment to unlock this building</p>
                      </div>
                    )}

                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div 
                          className={`flex-1 ${isLocked ? 'opacity-60' : 'cursor-pointer'}`}
                          onClick={() => !isLocked && handleBuildingSelect(building.hoa_id)}
                        >
                          <CardTitle className="text-lg flex items-center gap-2">
                            {building.name}
                            {isLocked && <Lock className="w-4 h-4 text-amber-600" />}
                          </CardTitle>
                          <div className="flex items-center gap-1 text-slate-500 text-sm mt-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{building.address || building.country}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBuilding(building.hoa_id, building.name);
                            }}
                            data-testid={`delete-building-${building.hoa_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {!isLocked && (
                            <ChevronRight 
                              className="w-5 h-5 text-slate-400 cursor-pointer" 
                              onClick={() => handleBuildingSelect(building.hoa_id)}
                            />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className={isLocked ? 'opacity-60' : 'cursor-pointer'} onClick={() => !isLocked && handleBuildingSelect(building.hoa_id)}>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Wrench className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Tickets</p>
                            <p className="font-semibold text-slate-900">{building.stats?.open_tickets || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                            <Bell className="w-4 h-4 text-sky-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Notices</p>
                            <p className="font-semibold text-slate-900">{building.stats?.recent_notices || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Documents</p>
                            <p className="font-semibold text-slate-900">{building.stats?.total_documents || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Home className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Units</p>
                            <p className="font-semibold text-slate-900">{building.stats?.total_units || 0}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Add Building Dialog */}
      <Dialog open={showAddBuildingDialog} onOpenChange={setShowAddBuildingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Building</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-indigo-800">
                <strong>Pricing:</strong> R35 per unit x 12 months = <span className="font-bold text-indigo-600">R420/unit/year</span>
              </p>
              <p className="text-sm text-indigo-800 mt-1">
                <strong>Commission:</strong> You'll earn <span className="font-bold text-indigo-600">10%</span> of the annual subscription when this building signs up.
              </p>
              {newBuilding.total_units && (
                <div className="mt-2 pt-2 border-t border-indigo-200">
                  <p className="text-sm text-indigo-900">
                    Annual subscription: <span className="font-bold">R{(parseInt(newBuilding.total_units || 0) * 35 * 12).toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-indigo-900">
                    Your commission: <span className="font-bold text-emerald-600">R{(parseInt(newBuilding.total_units || 0) * 35 * 12 * 0.10).toLocaleString()}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Building Name *</Label>
                <Input
                  value={newBuilding.name}
                  onChange={(e) => setNewBuilding({...newBuilding, name: e.target.value})}
                  placeholder="e.g., Sunset Heights"
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Address *</Label>
                <Input
                  value={newBuilding.address}
                  onChange={(e) => setNewBuilding({...newBuilding, address: e.target.value})}
                  placeholder="123 Main Street"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={newBuilding.city}
                  onChange={(e) => setNewBuilding({...newBuilding, city: e.target.value})}
                  placeholder="Cape Town"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Total Units *</Label>
                <Input
                  type="number"
                  value={newBuilding.total_units}
                  onChange={(e) => setNewBuilding({...newBuilding, total_units: e.target.value})}
                  placeholder="24"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Building Admin Contact</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admin Name</Label>
                  <Input
                    value={newBuilding.admin_name}
                    onChange={(e) => setNewBuilding({...newBuilding, admin_name: e.target.value})}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Admin Email *</Label>
                  <Input
                    type="email"
                    value={newBuilding.admin_email}
                    onChange={(e) => setNewBuilding({...newBuilding, admin_email: e.target.value})}
                    placeholder="admin@building.com"
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Admin Phone</Label>
                  <Input
                    value={newBuilding.admin_phone}
                    onChange={(e) => setNewBuilding({...newBuilding, admin_phone: e.target.value})}
                    placeholder="+27 82 123 4567"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBuildingDialog(false)}>Cancel</Button>
            <Button onClick={handleAddBuilding} disabled={addingBuilding}>
              {addingBuilding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Building
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Request Review Dialog */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Building Transfer Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransfer && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{selectedTransfer.building_name}</h3>
                    <p className="text-sm text-slate-500">{selectedTransfer.building_address}</p>
                    <p className="text-sm text-slate-600 mt-1">{selectedTransfer.building_units} units</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>{selectedTransfer.requesting_user_name}</strong> ({selectedTransfer.requesting_user_email}) 
                  wants to transfer this building to your company.
                </p>
                {selectedTransfer.notes && (
                  <p className="text-sm text-amber-700 mt-2 italic">"{selectedTransfer.notes}"</p>
                )}
              </div>

              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm font-medium text-indigo-800 mb-2">What happens when you accept:</p>
                <ul className="text-xs text-indigo-700 space-y-1">
                  <li>• Building added to your portfolio</li>
                  <li>• All existing data preserved (tickets, documents, residents)</li>
                  <li>• Trustee retains access</li>
                  <li>• You become responsible for subscription payments</li>
                </ul>
              </div>

              <div>
                <Label>Response Notes (Optional)</Label>
                <Textarea
                  value={transferNotes}
                  onChange={(e) => setTransferNotes(e.target.value)}
                  placeholder="Add any notes for the trustee..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              {/* All Pending Transfers List */}
              {pendingTransfers.length > 1 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-2">Other pending requests:</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {pendingTransfers.filter(t => t.transfer_id !== selectedTransfer.transfer_id).map(transfer => (
                      <button
                        key={transfer.transfer_id}
                        className="w-full text-left p-2 rounded border hover:bg-slate-50 text-sm"
                        onClick={() => setSelectedTransfer(transfer)}
                      >
                        <span className="font-medium">{transfer.building_name}</span>
                        <span className="text-slate-500"> from {transfer.requesting_user_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowTransferModal(false);
              setSelectedTransfer(null);
              setTransferNotes('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleTransferAction('reject')}
              disabled={processingTransfer}
              data-testid="reject-transfer-btn"
            >
              {processingTransfer ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleTransferAction('accept')}
              disabled={processingTransfer}
              data-testid="accept-transfer-btn"
            >
              {processingTransfer ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Accept Transfer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
